import React from "react";
import { ArrowUp, Mic, MicOff, Play, RotateCcw, X } from "lucide-react";
import MicLevelVisualizer from "../../../../components/MicLevelVisualizer/MicLevelVisualizer";
import { PracticeAssistNote } from "../PracticeAssist/PracticeAssist";
import {
  isRealtimeUpgradeRequired,
  realtimeUpgradeMessage,
} from "../../../../utils/realtimeUpgradeState";
import "./MeetingRoomControls.css";

export default function MeetingRoomControls({
  sessionActive,
  isConnecting,
  isTrainingMode,
  trainingAnswerState,
  isAiSpeaking,
  isMuted,
  onStartSession,
  onToggleMute,
  onEndSession,
  onStartTrainingAnswer,
  onSubmitTrainingAnswer,
  onRestartTrainingAnswer,
  micVolumeRef,
  sessionError,
  isEntitlementExhausted,
  onUpgrade,
  practiceAssistMessage,
  practiceAssistVersion,
}) {
  const showUpgradeNotice =
    !sessionActive &&
    (isEntitlementExhausted || isRealtimeUpgradeRequired(sessionError));
  const upgradeNoticeMessage = sessionError
    ? realtimeUpgradeMessage(sessionError)
    : "You have no remaining time left. Upgrade to keep practicing.";
  const isTrainingRecording = trainingAnswerState === "recording";
  const isTrainingProcessing = trainingAnswerState === "processing";
  const showTrainingRecordHint =
    sessionActive &&
    isTrainingMode &&
    trainingAnswerState === "idle" &&
    !isAiSpeaking &&
    !isConnecting;

  const handleTrainingPrimaryAction = () => {
    if (isTrainingRecording) {
      onSubmitTrainingAnswer?.();
      return;
    }
    onStartTrainingAnswer?.();
  };

  return (
    <div className={`mr-controls-wrap ${isTrainingMode ? "mr-controls-wrap--training" : ""}`}>
      {showUpgradeNotice ? (
        <div className="mr-upgrade-notice" role="status">
          <span className="mr-upgrade-notice-text">{upgradeNoticeMessage}</span>
          <button
            type="button"
            className="mr-upgrade-notice-button"
            onClick={onUpgrade}
          >
            Upgrade
          </button>
        </div>
      ) : null}

      {sessionActive && !isTrainingMode ? (
        <PracticeAssistNote key={practiceAssistVersion} message={practiceAssistMessage} />
      ) : null}

      {sessionActive && (!isTrainingMode || isTrainingRecording) ? (
        <MicLevelVisualizer
          volumeRef={micVolumeRef}
          muted={isTrainingMode ? false : isMuted}
        />
      ) : null}

      {showTrainingRecordHint ? (
        <div className="mr-training-record-hint">Tap to record</div>
      ) : null}

      <div className={`mr-controls-bar ${isTrainingMode && sessionActive ? "mr-training-controls-bar" : ""}`}>
        {!sessionActive ? (
          <button
            className="mr-control-button mr-control-button--start"
            onClick={onStartSession}
            title="Start Session"
            disabled={isConnecting || isEntitlementExhausted}
          >
            <Play size={34} fill="currentColor" />
          </button>
        ) : isTrainingMode ? (
          <>
            {isTrainingRecording ? (
              <button
                className="mr-control-button mr-training-secondary-button mr-training-retry-button"
                onClick={onRestartTrainingAnswer}
                title="Restart recording"
                aria-label="Restart recording"
              >
                <RotateCcw size={28} />
              </button>
            ) : null}

            <button
              className={`mr-control-button mr-training-primary-button ${isTrainingProcessing ? "mr-training-primary-button--disabled" : ""}`}
              onClick={handleTrainingPrimaryAction}
              title={isTrainingRecording ? "Send answer" : "Record answer"}
              aria-label={isTrainingRecording ? "Send answer" : "Record answer"}
              disabled={isTrainingProcessing}
            >
              {isTrainingRecording ? <ArrowUp size={40} /> : <Mic size={38} />}
            </button>

            <button
              className="mr-control-button mr-training-secondary-button mr-control-button--end"
              onClick={onEndSession}
              title="End training session"
              aria-label="End training session"
            >
              <X size={32} />
            </button>
          </>
        ) : (
          <>
            <button
              className="mr-control-button"
              onClick={onToggleMute}
              title={isMuted ? "Unmute Audio" : "Mute Audio"}
            >
              {isMuted ? (
                <MicOff size={34} style={{ color: "#b81a2f" }} />
              ) : (
                <Mic size={34} style={{ color: "#1a8fff" }} />
              )}
            </button>

            <button
              className="mr-control-button mr-control-button--end"
              onClick={onEndSession}
              title="End Session"
            >
              <X size={34} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
